import type { ReactNode } from 'react';
import { XCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatShortWhen } from './formatWhen';
import { DeleteRunButton } from './MeshStage';
import { runNumberOf, type Job3D } from './threeDApi';

// A RUN THAT MADE NO MESH, where its mesh would be — picked from its tile in the strip (see
// MeshRunStrip's `failed`), or shown on its own as the newest thing that happened.
//
// It was a line of red under the generate button, inside the settings window, which is the one
// place nobody looks once a run is over: a set whose script stopped itself ("Barrel nut is not a
// closed manifold") left the panel exactly as it was, as if the press had done nothing. A failure
// is a run like any other — it was billed, and its prompt and script are what is worth reading —
// so it has its tile, its frame, and its badge (`badge`, the caller's: each panel sends its own
// pictures).

// A TRACEBACK WITHOUT ITS UNDERLINES — the lines of ^ and ~ Python draws under the code a frame
// points at. They say nothing of their own, and an error stored before the build kept only its
// script's frames (see blender_ops) is two thousand characters of them over one line of message.
const withoutCarets = (error: string) =>
  error.split('\n').filter((line) => !/^\s*[\^~]+\s*$/.test(line)).join('\n');

// WHAT WENT WRONG, IN ITS OWN WORDS: the last line of a traceback, without its exception's name
// and without "The script did not run:" — "Barrel nut is not a closed manifold". The whole
// error stays one press away, for the line number and the frames above it.
function headlineOf(error: string): string {
  const lines = error.split('\n').map((line) => line.trim()).filter(Boolean);
  const last = (lines[lines.length - 1] ?? '').replace(/^The script did not run:\s*/, '');
  const said = last.match(/^[A-Za-z_.]*(?:Error|Exception)\s*:\s*(.+)$/);
  return said ? said[1] : last;
}

export function FailedRunPanel({ job, badge, deleting, onDelete, className }: {
  job: Job3D;
  badge?: ReactNode;
  deleting: boolean;
  onDelete: () => void;
  // The viewer's own box, so the panel does not jump when one replaces the other.
  className: string;
}) {
  const { t, language } = useLanguage();
  const full = job.error ? withoutCarets(job.error).trim() : '';
  const headline = full ? headlineOf(full) : t('notice.3d.failed');
  return (
    <div className={`${className} flex flex-col items-center justify-center gap-4 rounded-xl bg-slate-50 p-6 text-center`}>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-100">
        <XCircle className="h-5 w-5 text-rose-600" />
      </span>
      <div className="flex max-w-lg flex-col items-center gap-1">
        <p className="text-[11px] font-bold uppercase tracking-widest text-rose-600">
          {t('notice.3d.runFailedTitle').replace('{n}', String(runNumberOf(job)))}
        </p>
        <p className="text-base font-semibold leading-snug text-slate-800">{headline}</p>
        <p className="text-xs text-slate-400">
          {t('notice.3d.runFailedNoModel')}
          {job.createdAt && <span className="ml-2">{formatShortWhen(job.createdAt, language)}</span>}
        </p>
      </div>
      {/* THE WHOLE ERROR, folded: the headline is what happened, this is where — its line
          number and the frames above it, kept in their lines and set in mono so the markers stay
          under the code they point at. Not offered where it would only say the headline again. */}
      {full && full !== headline && (
        <details className="w-full max-w-2xl">
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
            {t('notice.3d.runFailedFull')}
          </summary>
          <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-white px-3 py-2 text-left font-mono text-[11px] leading-snug text-slate-600">
            {full}
          </pre>
        </details>
      )}
      <div className="flex items-center gap-2">
        {badge}
        <DeleteRunButton label={t('notice.3d.deleteRun')} busy={deleting} onClick={onDelete} />
      </div>
    </div>
  );
}
